/**
 * ConfigGuideModal — centered fullscreen-ish guide with tabs.
 * Shows contextual help + the full Context Prompts Guide.
 */

import { useState, useEffect } from 'react';
import type { TFunc } from '@/utils/tfunc';
import { useLanguage } from '@/hooks/useLanguage';
import './ConfigGuideModal.css';

type GuideSection = 'agent' | 'skill' | 'data-bundle';

interface ConfigGuideModalProps {
  section: GuideSection;
  visible: boolean;
  onClose: () => void;
}

// ─── Tab content ────────────────────────────────────────────────────────────

// A function of t rather than a constant: the tab LABELS translate, and the
// shared flow tab is injected per call instead of by mutating a module-level
// object once at import time.
// The markdown bodies live in the bundle under configGuide.md.* — they are
// several hundred lines of documentation, and a Hebrew admin reading an
// English manual inside a Hebrew product is the exact tell this whole effort
// exists to remove.
const tabContent = (t: TFunc): Record<GuideSection, { tabs: { id: string; label: string; content: string }[] }> => {
  const sections: Record<GuideSection, { tabs: { id: string; label: string; content: string }[] }> = {
  'data-bundle': {
    tabs: [
      {
        id: 'overview',
        label: t('configGuide.dataBundleGuide'),
        content: t('configGuide.md.dataBundle'),
      },
      { id: 'flow', label: t('configGuide.contextPromptsFlow'), content: '' }, // filled below
    ],
  },

  skill: {
    tabs: [
      {
        id: 'overview',
        label: t('configGuide.skillGuide'),
        content: t('configGuide.md.skill'),
      },
      { id: 'flow', label: t('configGuide.contextPromptsFlow'), content: '' },
    ],
  },

  agent: {
    tabs: [
      {
        id: 'overview',
        label: t('configGuide.agentGuide'),
        content: t('configGuide.md.agent'),
      },
      { id: 'flow', label: t('configGuide.contextPromptsFlow'), content: '' },
    ],
  },
  };
  // the flow tab is shared verbatim across every section
  for (const section of Object.values(sections)) {
    const flowTab = section.tabs.find(tab => tab.id === 'flow');
    if (flowTab) flowTab.content = t('configGuide.md.flow');
  }
  return sections;
};





// ─── Markdown renderer (line-by-line parser) ────────────────────────────────

function inlineFormat(text: string): string {
  return text
    // inline code is an identifier — same reason as the fenced blocks
    .replace(/`([^`]+)`/g, '<code class="guide-inline-code" dir="ltr">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
}

function renderMarkdown(md: string): string {
  const lines = md.split('\n');
  const out: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Code block
    if (line.trim().startsWith('```')) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing ```
      // dir="ltr" on the fence, always: these blocks hold ASCII flow diagrams
      // drawn with | and +, and a right-to-left <pre> reorders every line into
      // a picture of nothing. Code reads left-to-right in every language.
      out.push(`<pre class="guide-code" dir="ltr">${codeLines.join('\n')}</pre>`);
      continue;
    }

    // Table (line starts with |)
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      const tableRows: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        tableRows.push(lines[i].trim());
        i++;
      }
      if (tableRows.length >= 2) {
        // First row = header, second row = separator, rest = body
        const headerCells = tableRows[0].split('|').filter(c => c.trim()).map(c => `<th>${inlineFormat(c.trim())}</th>`);
        out.push('<table><thead><tr>' + headerCells.join('') + '</tr></thead><tbody>');
        for (let r = 2; r < tableRows.length; r++) {
          const cells = tableRows[r].split('|').filter(c => c.trim()).map(c => `<td>${inlineFormat(c.trim())}</td>`);
          out.push('<tr>' + cells.join('') + '</tr>');
        }
        out.push('</tbody></table>');
      }
      continue;
    }

    // H2
    if (line.startsWith('## ')) {
      out.push(`<h3 class="guide-h2">${inlineFormat(line.slice(3))}</h3>`);
      i++;
      continue;
    }

    // H3
    if (line.startsWith('### ')) {
      out.push(`<h4 class="guide-h3">${inlineFormat(line.slice(4))}</h4>`);
      i++;
      continue;
    }

    // Unordered list
    if (line.trimStart().startsWith('- ')) {
      out.push('<ul>');
      while (i < lines.length && lines[i].trimStart().startsWith('- ')) {
        out.push(`<li>${inlineFormat(lines[i].trimStart().slice(2))}</li>`);
        i++;
      }
      out.push('</ul>');
      continue;
    }

    // Ordered list
    if (/^\d+\.\s/.test(line.trimStart())) {
      out.push('<ol>');
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trimStart())) {
        const text = lines[i].trimStart().replace(/^\d+\.\s/, '');
        out.push(`<li>${inlineFormat(text)}</li>`);
        i++;
      }
      out.push('</ol>');
      continue;
    }

    // Horizontal rule
    if (line.trim() === '---') {
      out.push('<hr class="guide-hr" />');
      i++;
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Paragraph (collect consecutive non-empty lines)
    const paraLines: string[] = [];
    while (i < lines.length && lines[i].trim() !== '' && !lines[i].startsWith('#') && !lines[i].startsWith('```') && !lines[i].trimStart().startsWith('- ') && !/^\d+\.\s/.test(lines[i].trimStart()) && !(lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) && lines[i].trim() !== '---') {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length > 0) {
      out.push(`<p>${inlineFormat(paraLines.join(' '))}</p>`);
    }
  }

  return out.join('\n');
}

// ─── Component ──────────────────────────────────────────────────────────────

export const ConfigGuideModal: React.FC<ConfigGuideModalProps> = ({ section, visible, onClose }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (visible) {
      setActiveTab('overview');
      requestAnimationFrame(() => setShow(true));
    } else {
      setShow(false);
    }
  }, [visible]);

  if (!visible) return null;

  const tabs = tabContent(t)[section].tabs;
  const currentTab = tabs.find(t => t.id === activeTab) || tabs[0];

  return (
    <div className={`guide-overlay ${show ? 'guide-visible' : ''}`} onClick={onClose}>
      <div className={`guide-modal ${show ? 'guide-modal-visible' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="guide-header">
          <div className="guide-header-icon">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
            </svg>
          </div>
          <h2 className="guide-title">{t('configGuide.title')}</h2>
          <button className="guide-close" onClick={onClose} aria-label={t('configGuide.closeGuide')}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="guide-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`guide-tab ${activeTab === tab.id ? 'guide-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div
          className="guide-body"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(currentTab.content) }}
        />
      </div>
    </div>
  );
};

/**
 * Small (i) button to place in modal headers.
 */
export const GuideInfoButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const { t } = useLanguage();
  return (
  <button
    type="button"
    className="guide-info-btn"
    onClick={onClick}
    title={t('configGuide.openGuide')}
    aria-label={t('configGuide.openGuide')}
  >
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  </button>
  );
};
